import { useTranslation } from "react-i18next";

import type { CcPackageSummary, CcReadyGate } from "@/api/code-companion";

/**
 * O cabeçalho do trace: o contexto do pacote, sempre à vista (S-064 onda 7).
 *
 * No protótipo o hero é a âncora da tela — navy, com o pacote, a contagem de objetos, o
 * change set e o alvo numa linha monospace. Ele responde "onde eu estou" sem que
 * ninguém precise rolar ou abrir nada, que é o que faltava quando tudo vivia dentro de
 * uma gaveta.
 *
 * **As cores são tokens, não hex.** ✅ O `_ds_manifest.json` do bundle declara que o
 * design system foi extraído do próprio app: `#08254c` é `--exed-navy` e `#ffc83d` é
 * `--exed-gold`. Copiar hex aqui criaria uma segunda paleta que sai de sincronia na
 * primeira mudança de tema.
 *
 * **O gate fica no hero, à direita.** É a pergunta que o pacote inteiro serve para
 * responder — "posso promover?" — e mantê-la visível evita que alguém precise procurá-la
 * depois de rolar dez painéis.
 */

/** O tom do botão do gate, pelo veredito. Sem gate lido ainda, neutro. */
export function gateTone(gate: CcReadyGate | undefined): {
  bg: string;
  bd: string;
  fg: string;
  dot: string;
} {
  if (!gate) {
    return {
      bg: "rgba(255,255,255,.08)",
      bd: "rgba(255,255,255,.18)",
      fg: "rgba(255,255,255,.75)",
      dot: "rgba(255,255,255,.4)",
    };
  }
  return gate.ok
    ? {
        bg: "rgba(39,161,85,.16)",
        bd: "rgba(39,161,85,.45)",
        fg: "#8ce0ad",
        dot: "var(--exed-success)",
      }
    : {
        bg: "rgba(242,115,37,.14)",
        bd: "rgba(242,115,37,.4)",
        fg: "#ffc9a3",
        dot: "var(--exed-warn)",
      };
}

interface Props {
  pkg: CcPackageSummary;
  changeSetId?: number | null;
  gate?: CcReadyGate;
  onOpenGate?: () => void;
  /** A ação primária do hero. No protótipo é o botão dourado à direita. */
  action?: React.ReactNode;
}

export default function TraceHero({ pkg, changeSetId, gate, onOpenGate, action }: Props) {
  const { t } = useTranslation();
  const tom = gateTone(gate);
  const faltando = gate?.missing?.length ?? 0;

  const meta = (label: string, value: string) => (
    <span>
      <span style={{ color: "rgba(255,255,255,.55)" }}>{label}</span>{" "}
      <strong style={{ color: "rgba(255,255,255,.92)", fontWeight: 500 }}>{value}</strong>
    </span>
  );
  const ponto = <span style={{ color: "rgba(255,255,255,.25)" }}>·</span>;

  return (
    <div
      data-testid="trace-hero"
      className="relative overflow-hidden px-6 pb-4 pt-5"
      style={{ background: "var(--exed-navy)", color: "#fff" }}
    >
      {/* A malha e os halos do protótipo. Decoração pura, e por isso `pointer-events:none`
          e `aria-hidden`: nada aqui é conteúdo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.04) 1px,transparent 1px)," +
            "linear-gradient(90deg,rgba(255,255,255,.04) 1px,transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at center,#000 20%,transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse at center,#000 20%,transparent 78%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          width: 760,
          height: 760,
          left: -220,
          top: -320,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(255,200,61,.13) 0%,rgba(255,200,61,0) 60%)",
        }}
      />

      <div className="relative z-[2] flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          <div
            className="mb-1.5 flex items-center gap-2 uppercase"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "9px",
              color: "var(--exed-gold)",
              letterSpacing: ".26em",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--exed-gold)",
                boxShadow: "0 0 9px var(--exed-gold)",
              }}
            />
            {t("Realize · custom code")}
          </div>

          <h1
            className="m-0 flex items-center gap-3"
            style={{
              fontFamily: "var(--font-brand)",
              fontWeight: 800,
              fontSize: "26px",
              lineHeight: 1.05,
              letterSpacing: "-0.022em",
            }}
          >
            {t("Code Companion")}
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "9px",
                fontWeight: 600,
                padding: "4px 9px",
                borderRadius: 999,
                background: "rgba(255,255,255,.08)",
                border: "1px solid rgba(255,255,255,.15)",
                color: "rgba(255,255,255,.8)",
                letterSpacing: ".1em",
              }}
            >
              TRACE
            </span>
          </h1>

          <div
            className="mt-2 flex flex-wrap items-center gap-3"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "11px",
              color: "rgba(255,255,255,.55)",
              letterSpacing: ".03em",
            }}
          >
            {meta(t("pkg"), pkg.devclass)}
            {ponto}
            {/* Com denominador: "12" sozinho passa a fatia por total do pacote, e é a
                âncora da tela. */}
            {meta(t("objects"), `${pkg.in_scope_count}/${pkg.object_count}`)}
            {/* Change set ausente é estado real: o pacote existe antes de haver um. */}
            {changeSetId ? (
              <>
                {ponto}
                {meta(t("change set"), `CS-${changeSetId}`)}
              </>
            ) : null}
            {pkg.title ? (
              <>
                {ponto}
                {meta(t("title"), pkg.title)}
              </>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2.5">
        {/* Sem `onOpenGate` é um selo, não um botão: controle focável que não faz nada
            gasta uma parada de Tab num beco sem saída e promete uma ação inexistente. */}
        <button
          type="button"
          data-testid="hero-gate"
          onClick={onOpenGate}
          disabled={!onOpenGate}
          aria-disabled={!onOpenGate}
          tabIndex={onOpenGate ? undefined : -1}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2"
          style={{
            background: tom.bg,
            border: `1px solid ${tom.bd}`,
            color: tom.fg,
            cursor: onOpenGate ? "pointer" : "default",
          }}
        >
          <span
            style={{ width: 7, height: 7, borderRadius: 999, background: tom.dot }}
          />
          <span
            className="uppercase"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "10px",
              letterSpacing: ".1em",
              fontWeight: 600,
            }}
          >
            {t("Ready for deploy")} ·{" "}
            {gate ? (gate.ok ? t("clear") : t("blocked")) : t("checking")}
          </span>
          {/* A contagem é do que FALTA, não do que passou: é o número acionável. */}
          {faltando > 0 && (
            <span data-testid="hero-gate-missing" style={{ fontSize: "11px", opacity: 0.75 }}>
              {faltando}
            </span>
          )}
        </button>
        {action}
        </div>
      </div>
    </div>
  );
}
